import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { TESTIMONIALS_FALLBACK, type ApiTestimonial } from "@/lib/testimonials-fallback";
import Navigation from "@/components/navigation";
import HeroSection from "@/components/hero-section";
import PressLogos from "@/components/press-logos";
import HowItWorks from "@/components/how-it-works";
import FeaturesDashboard from "@/components/features-dashboard";
import Testimonials from "@/components/testimonials";
import RecentValuationsTicker from "@/components/recent-valuations-ticker";
import CTASection from "@/components/cta-section";
import Footer from "@/components/footer";
import SEOHead from "@/components/seo-head";
import SEOFaq from "@/components/seo-faq";
import ChatbotWidget from "@/components/chatbot-widget";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-load the heavy valuation calculator (sliders, charts, photo
// uploads, OTP modal) so the home page's initial JS budget stays small.
const ValuationCalculator = lazy(() => import("@/components/valuation-calculator"));

function ValuationCalculatorSkeleton() {
  return (
    <section id="valuation" className="py-16 sm:py-24 bg-gradient-to-br from-background to-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <Skeleton className="h-8 w-72 mx-auto mb-3" />
          <Skeleton className="h-5 w-96 mx-auto" />
        </div>
        <div className="bg-white rounded-2xl p-6 sm:p-8 apple-shadow space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useTranslation();

  const { data: testimonials } = useQuery<ApiTestimonial[]>({
    queryKey: ["/api/testimonials", { featured: 1 }],
    queryFn: async () => {
      const res = await fetch("/api/testimonials?featured=1");
      if (!res.ok) throw new Error("Failed to load testimonials");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Keep JSON-LD reviews aligned with what the Testimonials component
  // actually renders: when the API returns nothing we fall back to the
  // same in-bundle list, so visible content and structured data stay in
  // sync (avoids Google's "review snippet markup not on page" warning).
  const reviewSource =
    testimonials && testimonials.length > 0 ? testimonials : TESTIMONIALS_FALLBACK;
  const reviews = reviewSource.slice(0, 6);
  const ratingValue = reviews.length
    ? Number(
        (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1),
      )
    : null;

  const organization: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://realtyblocks.co.in/#organization",
    "name": "RealtyBlocks",
    "description": "India's first AI-powered real estate operating system",
    "url": "https://realtyblocks.co.in",
    "logo": "https://realtyblocks.co.in/assets/images/realtyblocks-logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-9999999999",
      "contactType": "customer service",
      "availableLanguage": ["English", "Hindi"],
    },
    "address": { "@type": "PostalAddress", "addressCountry": "IN" },
    "serviceArea": { "@type": "Country", "name": "India" },
    "sameAs": [
      "https://www.facebook.com/realtyblocks",
      "https://www.linkedin.com/company/realtyblocks",
      "https://twitter.com/realtyblocks",
    ],
  };

  if (reviews.length > 0 && ratingValue != null) {
    organization.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingValue,
      "bestRating": 5,
      "worstRating": 1,
      "ratingCount": reviews.length,
      "reviewCount": reviews.length,
    };
    organization.review = reviews.map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "reviewBody": r.quote,
      "itemReviewed": { "@id": "https://realtyblocks.co.in/#organization" },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating,
        "bestRating": 5,
        "worstRating": 1,
      },
      ...(r.city
        ? { "locationCreated": { "@type": "Place", "name": r.city } }
        : {}),
    }));
  }

  const structuredData: object[] = [
    organization,
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://realtyblocks.co.in/#website",
      "url": "https://realtyblocks.co.in",
      "name": "RealtyBlocks",
      "description": "India's first AI-powered real estate operating system",
      "inLanguage": ["en-IN", "hi-IN"],
      "publisher": { "@id": "https://realtyblocks.co.in/#organization" },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate":
            "https://realtyblocks.co.in/buy?q={search_term_string}",
        },
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return (
    <div className="bg-white text-foreground overflow-x-hidden pb-20 lg:pb-0">
      <SEOHead
        title={t("seo.homeTitle")}
        description={t("seo.homeDesc")}
        keywords="real estate India, AI real estate platform, property listings India, NRI property investment India, buy property India, sell property India, rent apartment India, verified properties India, property search India, real estate marketplace India, AI property valuation, property negotiation India, zero brokerage property"
        canonicalUrl="https://realtyblocks.co.in/"
        structuredData={structuredData}
      />
      <Navigation />
      <HeroSection />
      <PressLogos />
      <RecentValuationsTicker />
      <Suspense fallback={<ValuationCalculatorSkeleton />}>
        <ValuationCalculator />
      </Suspense>
      <HowItWorks />
      <FeaturesDashboard />
      <Testimonials />
      <SEOFaq />
      <CTASection />
      <Footer />
      <ChatbotWidget />
    </div>
  );
}
